/* /assets/recently-viewed.js
 *
 * Reads the "recently viewed" product handles tracked in localStorage
 * (snippets/recently-viewed-track.liquid), fetches each product via
 * Shopify's public /products/{handle}.js endpoint, and renders cards
 * built from the same markup/classes as snippets/product-card.liquid
 * into any [data-recently-viewed] section on the page.
 */
(function () {
  var STORAGE_KEY = 'speedyrc:recentlyViewed';

  function getStoredHandles() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var handles = raw ? JSON.parse(raw) : [];
      return Array.isArray(handles) ? handles : [];
    } catch (e) {
      return [];
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return (
        {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        }[char] || char
      );
    });
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }
    // Fallback if theme.js hasn't loaded for some reason.
    return '$' + (cents / 100).toFixed(2);
  }

  // Mirrors snippets/price-split.liquid: splits a formatted "$X.YZ" price into
  // currency/whole/cents spans so the CSS that sizes them differently still
  // applies. Falls back to the plain formatted string for non-"$" currencies,
  // same as price-split.liquid does (see that file for why).
  //
  // formatMoney()'s output is trusted markup, not user content - shop.money_format
  // can itself contain HTML (e.g. Shopify's built-in "<span class=money>${{amount}}</span>"
  // format), same as Liquid's money filters, which product-card.liquid renders
  // unescaped. Don't escapeHtml() it, or that HTML shows up as visible text.
  function splitPrice(cents) {
    var formatted = formatMoney(cents);
    if (formatted.indexOf('$') === -1) {
      return formatted;
    }

    var plain = formatted.replace(/[^0-9.]/g, '');
    var parts = plain.split('.');
    var whole = parts[0] || '';
    var html = '<span class="price-currency">$</span><span class="price-whole">' + whole + '</span>';

    if (parts.length > 1) {
      html += '<span class="price-cents">.' + parts[parts.length - 1] + '</span>';
    }

    return html;
  }

  // Mirrors the price block in snippets/product-card.liquid: split current
  // price, plus an "RRP $X / SAVE $Y" line when on sale, plus the varying-price
  // "From $X" case - kept faithful to that markup (down to its literal
  // "From " + translation-that-also-says-"From" text) so cards match exactly.
  function buildPriceMarkup(product, strings) {
    if (!product.available) {
      return '<div class="product-card__availability">' + escapeHtml(strings.soldOut) + '</div>';
    }

    var onSale = product.compare_at_price > product.price;

    if (product.price_varies) {
      // Translation text and formatMoney() output are both trusted (see splitPrice) -
      // don't escapeHtml() the assembled string.
      var template = onSale ? strings.onSaleFromPrice : strings.fromPrice;
      var text = 'From ' + (template || '').replace('__PRICE__', formatMoney(product.price));
      return '<div class="product-card__price">' + text + '</div>';
    }

    var currentPrice =
      '<span class="visually-hidden">' + escapeHtml(strings.regularPrice) + '</span>' +
      '<span class="product-card__price-current">' + splitPrice(product.price) + '</span>';

    if (!onSale) {
      return '<div class="product-card__price">' + currentPrice + '</div>';
    }

    var saveAmount = product.compare_at_price - product.price;

    return (
      '<div class="product-card__price">' +
      currentPrice +
      '<span class="visually-hidden">' + escapeHtml(strings.salePrice) + '</span>' +
      '<div class="product-card__price-rrp">' +
      'RRP <span class="product-card__price-compare">' + formatMoney(product.compare_at_price) + '</span>' +
      '<span class="product-card__price-save">SAVE ' + formatMoney(saveAmount) + '</span>' +
      '</div>' +
      '</div>'
    );
  }

  function renderCard(product, layout, strings) {
    var image = product.featured_image || (product.images && product.images[0]) || null;
    // product-card.liquid reserves space via padding-top sized to the image's real
    // aspect ratio (image.aspect_ratio) so the absolutely-positioned <img> (width:100%;
    // height:100%) never stretches it. /products/{handle}.js doesn't give us that on
    // featured_image directly, but the first media entry (the featured image) carries
    // the same aspect_ratio Liquid's `image.aspect_ratio` would - fall back to a square
    // box only if that's ever missing.
    var media = product.media && product.media[0];
    var aspectRatio = (media && media.aspect_ratio) || 1;
    var paddingTop = (100 / aspectRatio).toFixed(4) + '%';
    var onSale = product.compare_at_price > product.price;
    var wrapperClass =
      layout === 'carousel' ? 'swiper-slide' : 'grid__item small--one-half medium-up--one-fifth';
    var title = escapeHtml(product.title);

    var priceMarkup = buildPriceMarkup(product, strings);

    var saleTag = onSale
      ? '<div class="product-tag product-tag--absolute" aria-hidden="true">' +
        escapeHtml(strings.onSale) +
        '</div>'
      : '';

    var imageMarkup = image
      ? '<img class="lazyload" data-src="' + image + '" data-sizes="auto" alt="' + title + '">'
      : '';

    return (
      '<div class="' +
      wrapperClass +
      '">' +
      '<a href="/products/' +
      product.handle +
      '" class="product-card" data-product-id="' +
      product.id +
      '">' +
      '<div class="product-card__image-container">' +
      '<div class="product-card__image-wrapper">' +
      '<div class="product-card__image">' +
      '<div style="padding-top: ' +
      paddingTop +
      ';">' +
      imageMarkup +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '<div class="product-card__info">' +
      '<div class="product-card__name">' +
      title +
      '</div>' +
      priceMarkup +
      '</div>' +
      saleTag +
      '<div class="product-card__overlay">' +
      '<span class="btn product-card__overlay-btn">' +
      escapeHtml(strings.view) +
      '</span>' +
      '</div>' +
      '</a>' +
      '</div>'
    );
  }

  function initCarousel(container) {
    if (!window.Swiper) {
      return;
    }

    new Swiper(container.querySelector('.recently-viewed__swiper'), {
      slidesPerView: 2,
      // Flush, no gap - matches the grid layout, which (like every other product
      // grid in this theme) uses grid--no-gutters and relies on .product-card's
      // own left border for separation instead of a margin between cards.
      spaceBetween: 0,
      navigation: {
        nextEl: container.querySelector('.swiper-button-next'),
        prevEl: container.querySelector('.swiper-button-prev')
      },
      breakpoints: {
        750: { slidesPerView: 4 },
        990: { slidesPerView: 5 }
      }
    });
  }

  function readStrings(container) {
    var el = container.querySelector('[data-recently-viewed-strings]');
    if (!el) {
      return {};
    }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  function init(container) {
    var currentHandle = window.speedyrcCurrentProductHandle;
    var limit = parseInt(container.getAttribute('data-limit'), 10) || 6;
    var layout = container.getAttribute('data-layout');
    var itemsEl = container.querySelector('[data-recently-viewed-items]');
    var strings = readStrings(container);

    var handles = getStoredHandles()
      .filter(function (handle) {
        return handle !== currentHandle;
      })
      .slice(0, limit);

    if (!handles.length || !itemsEl) {
      return;
    }

    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (products) {
      products = products.filter(Boolean);

      if (!products.length) {
        return;
      }

      itemsEl.innerHTML = products
        .map(function (product) {
          return renderCard(product, layout, strings);
        })
        .join('');

      container.hidden = false;

      if (layout === 'carousel') {
        initCarousel(container);
      }
    });
  }

  var containers = document.querySelectorAll('[data-recently-viewed]');
  for (var i = 0; i < containers.length; i++) {
    init(containers[i]);
  }
})();
